import * as userManagement from '@nrwl/rx-state-user';
import {
  ActivatedRoute,
  NavigationEnd,
  PRIMARY_OUTLET,
  Router,
  RouterModule,
} from '@angular/router';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  Inject,
  NgZone,
} from '@angular/core';
import { SetEndRouter, State } from '@nrwl/rx-user';
import { Store, select } from '@ngrx/store';
import { AuthService } from '@nrwl/rx-service-auth';
import { RxChurnzeroService } from '@nrwl/rx-service-churnzero';
import { Observable } from 'rxjs';
import { UserStoreActions } from '@nrwl/rx-state-user';
import { environment } from '@nrwl/environment';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SupportMenuComponent } from './core/components/support-menu/support-menu.component';
import { RxDialogComponent } from '@nrwl/rx-dialog-template';
import { PermissionDialogComponent } from './admin/admin-dialogs/permission-dialog';
import { DialogService } from '@nrwl/dialog-service';
import { ClinicLocationDialogConfigService } from '@nrwl/dialog-configs';
import { HttpClient } from '@angular/common/http';
import { hasLeadAccess } from '@nrwl/rx-root-state';
import { User } from '@sentry/browser';
import { CookieService } from 'ngx-cookie-service';
import { ImpersonateService } from './impersonate/impersonate.service';
import { DoctorService } from '@nrwl/rx-service-doctor';
import { filter, first, take } from 'rxjs/operators';
import { AdminDialogService } from 'libs/rx-services/src/lib/dialogs/admin/admin-dialog.service';
import { ClientLibraryNavService } from './clients/library/services/client-library-nav.service';
import { MatDialog } from '@angular/material/dialog';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RxListModule } from '@nrwl/rx-list';
import { MatMenuModule } from '@angular/material/menu';
import { RxNavBarModule } from '@nrwl/rx-navbar';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { MatButtonModule } from '@angular/material/button';
import { PlanCustomizationNavigationService } from './core/services/plan-customization-navigation.service';

@Component({
  selector: 'nrwl-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RxListModule,
    MatMenuModule,
    RxNavBarModule,
    LoadingBarModule,
    RouterModule,
    MatButtonModule,
    SupportMenuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Add standalone: true for Angular 19
  standalone: true,
})
export class AppComponent implements OnInit {
  public env: any = environment;
  public upload = false;
  public urlAfterRedirects: string;
  public loadingBarColor = '#0E62F9';
  public isStaticPage = false;
  private hasLeadsAccess: boolean;
  private originNavigationPath: string | null = null;

  // Declare the store property before using it in any initializers
  private store: Store<State>;

  public userOptions: any = [
    {
      type: 'knowledge-base',
      title: 'Knowledge Base',
      description: 'RxPhoto support documentation',
      url: 'https://patientnow.my.site.com/helpcenter/s/topic/0TO3s000000E4KbGAK/rxphoto-main',
    },
    {
      type: 'education-platform',
      title: 'ElevateNow: Education Platform',
      description: 'Training videos and educational materials',
      url: 'http://patient-now.skyprepapp.com/',
    },
  ];

  public userPhoto$: Observable<string>;
  public user$: Observable<User | any>;
  public email$: Observable<string>;
  public username$: Observable<string>;
  public hasLeadsAccessWP$: Observable<boolean>;
  public notificationAmount$: Observable<number>;
  public hasLeadsAccess$: Observable<boolean>;
  public doctor$: Observable<any>;

  constructor(
    private router: Router,
    private authService: AuthService,
    store: Store<State>, // Remove 'private' since we'll assign it to the class property
    private dialogService: DialogService,
    private dialogConfigService: ClinicLocationDialogConfigService,
    private changeDetectorRef: ChangeDetectorRef,
    private cookieService: CookieService,
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    private impersonateService: ImpersonateService,
    private doctorService: DoctorService,
    private route: ActivatedRoute,
    private zone: NgZone,
    private rxChurnzeroService: RxChurnzeroService,
    public dialog: MatDialog,
    private _dialogService: DialogService,
    private _adminDialogConfig: AdminDialogService,
    private clientLibraryNavService: ClientLibraryNavService,
    private planCustomizationNavigation: PlanCustomizationNavigationService,

    @Inject(DOCUMENT) private _document: HTMLDocument,
  ) {
    // Assign the store parameter to the class property
    this.store = store;

    // Initialize the Observables after store has been assigned
    this.userPhoto$ = this.store.pipe(select(userManagement.UserStoreSelector.getUserPhoto));
    this.user$ = this.store.pipe(select(userManagement.UserStoreSelector.getUser));
    this.email$ = this.store.pipe(select(userManagement.UserStoreSelector.getUserEmail));
    this.username$ = this.store.pipe(select(userManagement.UserStoreSelector.getUserName));
    this.hasLeadsAccessWP$ = this.store.pipe(
      select(userManagement.UserStoreSelector.hasLeadsAccess),
    );
    this.notificationAmount$ = this.store.pipe(
      select(userManagement.UserStoreSelector.getNotificationsAmount),
    );
    this.hasLeadsAccess$ = this.store.pipe(select(hasLeadAccess));
    this.doctor$ = this.store.pipe(select(userManagement.UserStoreSelector.getUser));
    router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        // Redirect incomplete routes to Angular 10
        const redirectPaths = [
          //'/v19/administration/client-record/dashboard/forms',
          '/v19/leads',
          //'/v19/inbox',
          //'/v19/galleries',
          //'/v19/administration/anatomy',
          //'/v19/clients/notification',
          //'/v19/administration/account-settings/dashboard/team',
          //'/v19/user-setting/dashboard/profile',
        ];
        const currentPath = window.location.href;
        const shouldRedirect = redirectPaths.some((path) => currentPath.includes(path));
        if (shouldRedirect) {
          const newPath = currentPath.replace('/v19/', '/');
          window.location.replace(newPath);
          return;
        }

        const url = window.location.pathname;
        this.isStaticPage = e.url.includes('static');

        let currentRoute = this.router.routerState.snapshot.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        const origin =
          currentRoute.data['preserveOriginNavigation'] === true
            ? this.planCustomizationNavigation.getOrigin(currentRoute.queryParamMap)
            : null;
        const segments = origin?.returnUrl.root.children[PRIMARY_OUTLET]?.segments;
        this.originNavigationPath = segments
          ? '/' + segments.map((segment) => segment.path).join('/')
          : null;

        // List of prohibited URLs
        const prohibitedUrls = ['logout', 'impersonate'];

        if (!prohibitedUrls.some((prohibitedUrl) => url.includes(prohibitedUrl))) {
          // Save the URL to localStorage only if it's not a logout or impersonate URL
          localStorage.setItem(this.env.localStorage.returnUrl, url);
        }

        this.urlAfterRedirects = url;
        this.store.dispatch(new SetEndRouter(url));
        this.changeDetectorRef.detectChanges();

        // this.updateUserOptions(e.url);
      }
    });
    this.hasLeadsAccess$.subscribe(
      (hasLeadsAccess: boolean | any) => (this.hasLeadsAccess = hasLeadsAccess),
    );

    this.zone.runOutsideAngular(() => {
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          first(),
        )
        .subscribe((data: any) => {
          const preloader = document.querySelector('.site-preloader');
          preloader.addEventListener('transitionend', (event: TransitionEvent | any) => {
            if (event.propertyName === 'opacity') {
              preloader.remove();
            }
          });
          preloader.classList.add('site-preloader__fade');
        });
    });
  }

  public lmsLogin(option): void {
    const url = `${this.env.baseAPIUrlv3}authenticate/lms/`;
    const dontShowSSO = JSON.parse(localStorage.getItem('dont-show-lms-SSO'));

    const openAuthLink = (data: any) => window.open(data.auth_link, '_blank');

    if (dontShowSSO) {
      this.http.post(url, {}).pipe(take(1)).subscribe(openAuthLink);
      return;
    }

    this._dialogService
      .prompt(RxDialogComponent, this._adminDialogConfig.lmsLogin())
      .subscribe((value) => {
        if (!value) return;

        this.http
          .post(url, {})
          .pipe(take(1))
          .subscribe((data: any) => {
            if (value?.showAgainSettings?.value) this.setShowAgainSettings(true);
            openAuthLink(data);
          });
      });
  }

  public setShowAgainSettings(value: boolean) {
    localStorage.setItem('dont-show-lms-SSO', JSON.stringify(value));
  }

  onBackToFormsManagementClicked() {
    this.router.navigate(['/administration/client-record/dashboard/forms']);
  }

  processImpersonation() {
    const items = location.pathname.split('/');
    let token, userId, patientId, redirectUrl;

    // Check if "sso" is present in the path
    if (items[2] === 'sso') {
      userId = items[3];
      token = items[4];
      patientId = items[5];
    } else {
      userId = items[2];
      token = items[3];
      patientId = items[4];
    }

    redirectUrl = '/?token=' + token + '&id=' + userId;

    this.authService.setToken(token);
    this.authService.setUserId(+userId);

    console.log('Processing impersonation...');

    setTimeout(() => {
      this.doctorService.getDoctor(userId).subscribe(
        (response) => {
          //this.userDataService.user = response;
          //this.userDataService.impersonate = true;
          this.cookieService.set('impersonate', 'true', undefined, '/');
          if (response?.clinic?.emr_integration) {
            redirectUrl += '&emr=true';
            this.cookieService.set('emrRestriction', 'true', undefined, '/');
            console.log('EMR Restriction set...');
          }
          console.log('Doctor retrived...');

          window.location.href = redirectUrl;

          if (patientId) {
            //this.userDataService.saml_sso = true;
            //this.router.navigate(['base/patient', {id: patientId}]);
          } else {
            //this.userDataService.saml_sso = false;
            //this.router.navigate(['base/patients']);
          }
        },
        (error) => {
          console.log('Doctor retrival failed.', error);
          this.impersonateService.clearUserData();
          this.router.navigate(['logout']);
        },
      );
    });
  }

  ngOnInit() {
    this.setupChurnZero();
    const param: any = this.authService.getQueryParam();

    if (location.pathname.match('/impersonate')) {
      this.processImpersonation();
      return;
    }

    this.manageCookie('emrRestriction', param.emr, 'true');
    this.manageCookie('from_streamline', param.sl, 'true');
    this.manageCookie('facility', param.slf, param.slf);
    this.manageCookie('facilities', param.slfs, param.slfs);
    this.manageCookie('impersonate', param.imp, param.imp);
    this.manageCookie('sso_impersonate', param.sso_imp, param.sso_imp);
    this.manageCookie('envision_type', param.envisionType, param.envisionType);

    if (param?.schedulerToken && param?.id) {
      this.authService.setSchedulerToken(param.schedulerToken);
      this.authService.setUserId(param.id);
      return;
    }

    if (param.token && !param.schedulerToken) {
      this.authService.setToken(param.token);
      this.authService.setCompanyId(param.companyId);
    }

    if (!param.id) {
      this.authService.whoAmI().subscribe((value: User) => {
        console.log('whoAmI retrieved:', value);
        param.id = value.id;
        this.loadUserData(param);
      });
      return;
    }

    param.id = param.id ? param.id : localStorage.getItem(this.env.localStorage.userInfo);

    this.loadUserData(param);
  }

  private setupChurnZero() {
    this.user$.subscribe((user) => {
      if (!user) {
        return;
      }

      if (!user.clinic) {
        return;
      }

      this.rxChurnzeroService.loadChurnZero({
        accountExternalId: user.clinic.salesforce_id,
        contactExternalId: `${user.clinic.salesforce_id}.${user.email}`,
      });
    });
  }

  private manageCookie(cookieName: string, paramValue: any, valueToSet: any) {
    if (paramValue) {
      this.cookieService.set(cookieName, valueToSet, undefined, '/');
    } else {
      if (!this.cookieService.get(cookieName)) {
        this.cookieService.delete(cookieName, '/');
      }
    }
  }

  loadUserData(param) {
    if (param.token && !param.schedulerToken) {
      this.authService.setToken(param.token);
      this.authService.setCompanyId(param.companyId);
      this.authService.setUserId(Number(param.id));

      this.store.dispatch(new UserStoreActions.LoadNotificationCount());
      this.authService.returnUrl().then(() => {
        this.store.dispatch(new UserStoreActions.LoadUser(Number(param.id)));
        this.store.dispatch(new UserStoreActions.LoadLocations(Number(param.id)));
      });
    } else if (!param.token && param.id && !param.schedulerToken) {
      this.store.dispatch(new UserStoreActions.LoadUser(Number(param.id)));
      this.store.dispatch(new UserStoreActions.LoadLocations(Number(param.id)));
      this.store.dispatch(new UserStoreActions.LoadNotificationCount());
    }

    this.changeDetectorRef.detectChanges();
  }

  removeToken() {
    this.authService.authenticateLogout().subscribe();
  }

  navbarEvent(e: any) {
    if (e.type === 'logout') {
      this.authService.logout();
    } else if (e.type === 'user-setting') {
      this.router.navigate(['user-setting']);
    } else if (e.type === 'notifications') {
      this.router.navigate(['clients/notification']);
    } else if (e.type === 'inbox') {
      this.router.navigate(['inbox']);
    } else if (e.type === 'knowledge-base') {
      window.open(e.url, '_blank');
    } else if (e.type === 'education-platform') {
      this.lmsLogin(e);
    }
  }

  public onBackClicked() {
    const returnUrlExt = localStorage.getItem(environment.localStorage.returnUrlExt);
    if (returnUrlExt) {
      this.authService.clearStorage();
      document.location.href = decodeURIComponent(<any>returnUrlExt);
    } else {
      this.router.navigate(['/administration/client-record/dashboard/forms']);
    }
  }

  public onBackToClientList() {
    this.router.navigate(['/clients']);
  }

  public onBackToPatient() {
    this.clientLibraryNavService.requestBackToPatient();
  }

  public onBackToGalleries() {
    this.router.navigate(['galleries']);
  }

  public canShowNav() {
    // Prevent Navbar flicker on return to Altair
    if (!this.authService.isLoggedIn() || this.hasWordInPath('doctor-consultation')) {
      return false;
    }

    return (
      !this.canShowReturn() && !this.canShowClientLibraryReturn() && !this.canShowGalleriesReturn()
    );
  }

  public canShowHeader() {
    return !window.location.href.match('doctor-consultation');
  }

  public canShowSubNav() {
    // Prevent SubNav flicker on return to Altair
    if (!this.authService.isLoggedIn()) {
      return false;
    }
    // Display subnav for User Settings on transition from Altair
    if (this.hasWordInPath('user-setting')) {
      return true;
    }
    return !this.canShowReturn() && !this.canShowClientLibraryReturn();
  }

  public canShowReturn() {
    return this.hasWordInPath('editor');
  }

  public canShowClientLibraryReturn() {
    return this.hasWordInPath('clients/library');
  }

  // public canShowBackToClientList() {
  //     return this.hasWordInPath('inbox');
  // }

  public canShowGalleriesReturn() {
    return this.hasWordInPath('client-photos');
  }

  public isPatientRecordRoute() {
    return /^\/(?:v19\/)?patient\/\d+(?:\/)?$/.test(this.urlAfterRedirects || '');
  }

  hasWordInPath(word: string) {
    if (!this.urlAfterRedirects) {
      return false;
    }
    return (this.originNavigationPath ?? window.location.pathname).includes(word);
  }

  public isHeaderLinkActive(path: string, exact = false): boolean {
    if (this.originNavigationPath === null) {
      return this.router.isActive(path, exact);
    }

    return (
      this.originNavigationPath === path ||
      (!exact && this.originNavigationPath.startsWith(path + '/'))
    );
  }

  public navigateToLeads() {
    if (!this.hasLeadsAccess) {
      const config = this.dialogConfigService.permissionsInfo();
      (config as any).component = PermissionDialogComponent;
      this.dialogService.prompt(RxDialogComponent, config);
      return;
    }

    this.router.navigate(['leads']);
  }

  // #region Drag&Drop files

  // @HostListener('dragover', ['$event'])
  // onDragOver(event: DragEvent) {
  //     event.stopPropagation();
  //     event.preventDefault();
  //     if (this.containsFiles(event)) {
  //         this.upload = true;
  //     }
  // }

  onFilesChange() {
    this.upload = false;
  }

  onFileInvalids() {
    this.upload = false;
  }

  containsFiles(event) {
    if (event.dataTransfer.types) {
      for (let i = 0; i < event.dataTransfer.types.length; i++) {
        if (event.dataTransfer.types[i] === 'Files') {
          return true;
        }
      }
    }
    return false;
  }

  // #endregion

  // updateUserOptions(url = '') {
  //
  //     this.userOptions = [];
  //
  //     if (!url.includes('inbox')) {
  //         this.userOptions.push({
  //             id: 'inbox',
  //             title: 'Inbox',
  //             description: 'See forms of all clients'
  //         })
  //     }
  // }
}
